import i18n from "@/i18n";

const APP_DATE_INPUT_FORMATS: Record<string, string> = {
	en: "MM/DD/YYYY",
	"es-419": "DD/MM/YYYY"
};

const resolveAppDateLocale = (locale?: string): string => (
	locale && APP_DATE_INPUT_FORMATS[locale] ? locale : "en"
);

export const getAppDateInputFormat = (locale = i18n.language): string => (
	APP_DATE_INPUT_FORMATS[resolveAppDateLocale(locale)]
);

/** Returns YYYY-MM-DD for the given instant in the given IANA timezone. */
export const getDateInTimezone = (date: Date, timezone: string): string => {
	const formatter = new Intl.DateTimeFormat("en-CA", {
		timeZone: timezone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit"
	});
	return formatter.format(date);
};

/** Format a stored UTC due instant for display on the learner’s calendar day in `timeZone`. */
export const formatDueInstantInTimezone = (isoOrDate: string | Date, timeZone: string): string => {
	const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
	return d.toLocaleDateString(undefined, {timeZone});
};

export const formatDateForDisplay = (
	isoOrDate: string | Date | null | undefined,
	options?: Intl.DateTimeFormatOptions
): string => {
	if (!isoOrDate) return "";
	const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleDateString(resolveAppDateLocale(i18n.language), {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
		...options
	});
};

function isIsoOrDate(isoOrDate: string | Date): Date {
	return typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
}

/** True if the due instant’s calendar date in `timeZone` is strictly before today’s calendar date there. */
export const isDueCalendarOverdue = (isoOrDate: string | Date | null | undefined, timeZone: string): boolean => {
	if (!isoOrDate) return false;
	const dueDay = getDateInTimezone(isIsoOrDate(isoOrDate), timeZone);
	const today = getDateInTimezone(new Date(), timeZone);
	return dueDay < today;
};

/**
 * Format a plain calendar YYYY-MM-DD (no timezone on the value) for locale display.
 * Use when the API already resolved the learner-facing due day (e.g. admin feed).
 */
export const formatCalendarYmdForLocale = (ymd: string): string => {
	const parts = ymd.split("-").map((p) => parseInt(p, 10));
	if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return ymd;
	const [y, m, d] = parts;
	return new Date(y, m - 1, d).toLocaleDateString();
};

export const formatTimestamp = (timestamp: number): string => {
	if (timestamp === 0 || !timestamp) {
		return "00:00";
	}

	const hours = Math.floor(timestamp / 3600);
	const minutes = Math.floor((timestamp % 3600) / 60);
	const seconds = Math.floor(timestamp % 60);

	const formattedHours = String(hours).padStart(2, "0");
	const formattedMinutes = String(minutes).padStart(2, "0");
	const formattedSeconds = String(seconds).padStart(2, "0");

	if (hours < 1) {
		return `${formattedMinutes}:${formattedSeconds}`;
	}

	return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};